import { ReactNode, useId } from "react";
import { weatherSymbol, WeatherSymbol } from "../utils/weatherSymbol";
import "./weatherIcon.css";

interface WeatherIconProps {
    /// Forecast icon code, e.g. "sct.jpg"
    code: string | null | undefined;
    size?: number;
    /// Lighter colors for use on the dark hero
    onDark?: boolean;
}

/// Line-drawn weather icon. Announced to screen readers by its label ("Partly cloudy").
/// Renders an empty box of the same size when the code is missing, so layouts don't shift.
export function WeatherIcon({ code, size = 32, onDark = false }: WeatherIconProps) {
    const symbol = weatherSymbol(code);
    const className = "wx" + (onDark ? " wx--on-dark" : "");
    // Unique per icon, for the mask that hides the sun or moon behind a cloud
    const maskId = "wx-mask" + useId().replace(/[^a-zA-Z0-9_-]/g, "");

    if (!symbol) {
        return <span className={className} style={{ width: size, height: size }} aria-hidden="true" />;
    }

    return (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth={1.7}
            strokeLinecap="round"
            strokeLinejoin="round"
            role="img"
            aria-label={symbol.label}
        >
            <title>{symbol.label}</title>
            {drawing(symbol, maskId)}
        </svg>
    );
}

// Shapes, all on a 24×24 grid
const CLOUD = "M7 19a4 4 0 0 1-.6-8 5.5 5.5 0 0 1 10.6 1.5A3.3 3.3 0 0 1 17 19z";
const CLOUD_SMALL = "M9.5 20a3.4 3.4 0 0 1-.4-6.8 4.8 4.8 0 0 1 9.2 1.3A2.9 2.9 0 0 1 18.5 20z";
const CLOUD_HIGH = "M7 14.5a4 4 0 0 1-.6-8 5.5 5.5 0 0 1 10.6 1.5A3.3 3.3 0 0 1 17 14.5z";
const CLOUD_BACK = "M11 6.8A4.6 4.6 0 0 1 19.6 8a3.2 3.2 0 0 1 1.9 5.4";
const CLOUD_FRONT = "M6.5 20.5a3.6 3.6 0 0 1-.5-7.2 5 5 0 0 1 9.6 1.4 3 3 0 0 1-.1 5.8z";

function Sun() {
    return (
        <>
            <circle className="wx-sun-disc" cx="12" cy="12" r="4" />
            <path className="wx-sun" d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" />
        </>
    );
}

function Moon() {
    return <path className="wx-moon" d="M19.5 14.5A8 8 0 1 1 9.5 4.5a6.3 6.3 0 0 0 10 10z" />;
}

/// Small sun or moon in the top-left corner with a cloud in front of it. The mask
/// cuts the cloud's shape (plus a small gap) out of the sun or moon, so the lines
/// never cross whatever the background color is.
function BehindCloud({ night, cloud, maskId }: { night: boolean; cloud: string; maskId: string }) {
    return (
        <>
            <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                <rect width="24" height="24" fill="white" />
                <path d={cloud} fill="black" stroke="black" strokeWidth={4} />
            </mask>
            <g mask={`url(#${maskId})`}>
                {night
                    ? <path className="wx-moon" d="M8.6 2.8a4 4 0 1 0 4.6 5.4 3.2 3.2 0 0 1-4.6-5.4z" />
                    : (
                        <>
                            <circle className="wx-sun" cx="8" cy="8.5" r="3.4" />
                            <path className="wx-sun" d="M8 2.5v1.4M3.4 4.4l1 1M2 8.5h1.4M12.6 4.4l-1 1" />
                        </>
                    )}
            </g>
            <path className="wx-cloud" d={cloud} />
        </>
    );
}

function Drops({ count = 3 }: { count?: number }) {
    const xs = count === 2 ? [9, 15] : [8, 12, 16];
    return <path className="wx-rain" d={xs.map((x) => `M${x} 17.5l-1 3`).join("")} />;
}

function Flake({ x, y }: { x: number; y: number }) {
    return <path className="wx-snow" d={`M${x} ${y - 1.3}v2.6M${x - 1.3} ${y}h2.6`} />;
}

const CLOUD_SHOWERS = "M9.5 16a3.4 3.4 0 0 1-.4-6.8 4.8 4.8 0 0 1 9.2 1.3A2.9 2.9 0 0 1 18.5 16z";

function drawing(symbol: WeatherSymbol, maskId: string): ReactNode {
    const { kind, night } = symbol;
    switch (kind) {
        case "clear":
            return night ? <Moon /> : <Sun />;
        case "partly":
            return <BehindCloud night={night} cloud={CLOUD_SMALL} maskId={maskId} />;
        case "mostly":
            return <BehindCloud night={night} cloud={CLOUD} maskId={maskId} />;
        case "overcast":
            return <><path className="wx-cloud" d={CLOUD_BACK} /><path className="wx-cloud" d={CLOUD_FRONT} /></>;
        case "fog":
            return <path className="wx-cloud" d="M4 7h16M2.5 11h19M4 15h16M7 19h10" />;
        case "wind":
            return <path className="wx-cloud" d="M3 8h10.5a2.5 2.5 0 1 0-2.5-2.5M3 12h15.5a2.5 2.5 0 1 1-2.5 2.5M3 16h8" />;
        case "drizzle":
            return <><path className="wx-cloud" d={CLOUD_HIGH} /><path className="wx-rain wx-dots" d="M8.5 18.5h.01M12 20.5h.01M15.5 18.5h.01" /></>;
        case "rain":
            return <><path className="wx-cloud" d={CLOUD_HIGH} /><Drops /></>;
        case "showers":
            return (
                <>
                    <BehindCloud night={night} cloud={CLOUD_SHOWERS} maskId={maskId} />
                    <path className="wx-rain" d="M11.5 18l-1 3M15.5 18l-1 3" />
                </>
            );
        case "thunder":
            return <><path className="wx-cloud" d={CLOUD_HIGH} /><path className="wx-bolt" d="M12.8 15.5l-2.3 3.5h3l-2.3 3.5" /></>;
        case "snow":
            return <><path className="wx-cloud" d={CLOUD_HIGH} /><Flake x={8} y={19} /><Flake x={12} y={21} /><Flake x={16} y={19} /></>;
        case "sleet":
            return <><path className="wx-cloud" d={CLOUD_HIGH} /><path className="wx-rain" d="M8 17.5l-1 3M16 17.5l-1 3" /><Flake x={12} y={20} /></>;
    }
}
